/**
 * GoHighLevel inbound webhook dispatcher (browser-side).
 *
 * Each form posts directly to its specific GHL trigger URL after a
 * successful backend save. Failures are caught so the caller can
 * surface a user-friendly error message.
 */

export const GHL_WEBHOOKS = {
  // 3SM Workshop Request / Join A Workshop trigger
  workshop:        "https://services.leadconnectorhq.com/hooks/k3QonNwH8Irpa44GIYL7/webhook-trigger/c61d2031-6ba8-4238-8b2d-19cfe69384fc",

  // 3SM Executive Composure trigger
  executive:       "https://services.leadconnectorhq.com/hooks/k3QonNwH8Irpa44GIYL7/webhook-trigger/7a59c645-37eb-4b85-b8c0-7f49b7e856f7",

  // 3SM Youth Readiness trigger
  youth:           "https://services.leadconnectorhq.com/hooks/k3QonNwH8Irpa44GIYL7/webhook-trigger/feac1e50-c41d-4a2b-afef-9b8f706945ce",

  // 3SM Apply For Coaching / Private Coaching trigger
  privateCoaching: "https://services.leadconnectorhq.com/hooks/k3QonNwH8Irpa44GIYL7/webhook-trigger/118d7ff7-9ad5-4282-a06d-ff965fee8af4",

  // Self Defense Workshops trigger (used by Contact form when service_type === "workshop")
  selfDefense:     "https://services.leadconnectorhq.com/hooks/k3QonNwH8Irpa44GIYL7/webhook-trigger/04ba8b62-ecee-46ac-839a-5dcde2336c0b",
};

/** Map an IntakeForm `type` to the correct GHL webhook key. */
export function ghlKeyForIntake(type) {
  switch (type) {
    case "coaching":  return "privateCoaching";
    case "workshop":  return "workshop";
    case "youth":     return "youth";
    case "executive": return "executive";
    default:          return null;
  }
}

/** Map a Contact-form service_type to the correct GHL webhook key. */
export function ghlKeyForInquiry(serviceType) {
  switch (serviceType) {
    case "workshop":  return "selfDefense";
    case "executive": return "executive";
    case "private":   return "privateCoaching";
    case "corporate": return "selfDefense";
    default:          return null;
  }
}

/**
 * POST a flattened payload to the GHL inbound webhook.
 * Throws on network error or non-2xx response so the caller can surface UI errors.
 */
export async function postToGHL(webhookKey, payload) {
  const url = GHL_WEBHOOKS[webhookKey];
  if (!url) {
    throw new Error(`No GoHighLevel webhook configured for key "${webhookKey}"`);
  }

  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    // GHL inbound webhooks accept CORS; no credentials needed.
    mode: "cors",
  });

  if (!res.ok) {
    throw new Error(`GoHighLevel webhook responded ${res.status}`);
  }
  return res;
}

/** Build a flat lead payload from name/email/phone + form-specific extras + tracking. */
export function buildGhlPayload({ formType, name, email, phone, message = "", extras = {}, tracking = {} }) {
  const parts = (name || "").trim().split(/\s+/);
  const first_name = parts[0] || "";
  const last_name = parts.length > 1 ? parts.slice(1).join(" ") : "";
  return {
    first_name,
    last_name,
    full_name: name,
    email,
    phone,
    message,
    form_type: formType,
    lead_source:      tracking.lead_source      || "",
    utm_source:       tracking.utm_source       || "",
    utm_medium:       tracking.utm_medium       || "",
    utm_campaign:     tracking.utm_campaign     || "",
    utm_content:      tracking.utm_content      || "",
    utm_term:         tracking.utm_term         || "",
    referrer_url:     tracking.referrer_url     || "",
    landing_page_url: tracking.landing_page_url || "",
    current_page_url: tracking.current_page_url || "",
    ...extras,
  };
}
